import React, { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../../lib/api';
import { fmtDMY } from '../../lib/format';

/**
 * #7/#8 — multi-select MPRs across all visible projects, then tick specific open
 * lines with an editable order qty (≤ balance). onAdd(lines) receives PO-line
 * shaped rows carrying mpr_id / mpr_line_id / project_id.
 */
export function MprLinePicker({ projects = [], onAdd, preselect, testid = 'mpr-line-picker' }) {
  const [mprs, setMprs] = useState([]);
  const [f, setF] = useState({ project_id: '', category: '', required_by_to: '' });
  const [picked, setPicked] = useState(preselect?.mprIds || []);
  const [rows, setRows] = useState({});

  useEffect(() => {
    apiGet('/mpr-open-for-po', { project_id: f.project_id || undefined, category: f.category || undefined, required_by_to: f.required_by_to || undefined })
      .then(r => setMprs(r.items || [])).catch(() => setMprs([]));
  }, [f]);

  useEffect(() => {
    if (!preselect?.lines) return;
    const init = {};
    preselect.lines.forEach(l => { init[`${l.mpr_id}|${l.line_id}`] = { on: true, qty: l.balance_qty }; });
    setRows(init);
  }, [preselect]);

  const lines = useMemo(() => mprs.filter(m => picked.includes(m.id))
    .flatMap(m => m.line_items.map(l => ({ ...l, mpr_id: m.id, mpr_no: m.mpr_no, project_id: m.project_id, project_name: m.project_name }))), [mprs, picked]);
  const categories = [...new Set(mprs.map(m => m.category).filter(Boolean))];
  const key = (l) => `${l.mpr_id}|${l.line_id}`;
  const st = (l) => rows[key(l)] || { on: false, qty: l.balance_qty };
  const setRow = (l, patch) => setRows({ ...rows, [key(l)]: { ...st(l), ...patch } });
  const chosen = lines.filter(l => st(l).on);
  const sel = 'px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white';

  const add = () => {
    onAdd(chosen.map(l => ({
      material_id: l.material_id, material_name: l.material_name, unit: l.uom, qty: Number(st(l).qty) || 0,
      mpr_id: l.mpr_id, mpr_line_id: l.line_id, mpr_no: l.mpr_no, project_id: l.project_id, project_name: l.project_name,
      balance_qty: l.balance_qty,
    })));
    setRows({});
  };

  return (
    <div className="border border-indigo-200 bg-indigo-50/30 rounded-sm p-3 space-y-2" data-testid={testid}>
      <div className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872]">Pick MPR lines (any project)</div>
      <div className="grid grid-cols-3 gap-2">
        <select value={f.project_id} onChange={e => setF({ ...f, project_id: e.target.value })} className={sel} data-testid={`${testid}-project-filter`}>
          <option value="">All projects</option>{projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
        <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className={sel} data-testid={`${testid}-category-filter`}>
          <option value="">All categories</option>{categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <input type="date" value={f.required_by_to} onChange={e => setF({ ...f, required_by_to: e.target.value })} className={sel} title="Required by on/before" data-testid={`${testid}-reqby-filter`} />
      </div>
      <div className="max-h-28 overflow-y-auto bg-white border border-slate-200 rounded-sm" data-testid={`${testid}-mpr-list`}>
        {mprs.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No MPRs with an open balance</div>}
        {mprs.map(m => (
          <label key={m.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={picked.includes(m.id)} data-testid={`${testid}-mpr-${m.id}`}
              onChange={e => setPicked(e.target.checked ? [...picked, m.id] : picked.filter(x => x !== m.id))} />
            <span className="font-mono text-[#3b4872]">{m.mpr_no}</span> · {fmtDMY(m.created_at)} · {m.open_line_count} open item{m.open_line_count === 1 ? '' : 's'}
            {m.required_by && <> · required by {fmtDMY(m.required_by)}</>} · <span className="text-slate-500">{m.project_name}</span>
          </label>
        ))}
      </div>
      {lines.length > 0 && (
        <table className="w-full text-xs bg-white border border-slate-200" data-testid={`${testid}-lines`}>
          <thead><tr className="text-left text-slate-500 bg-slate-50">
            <th className="px-2 py-1" /><th className="px-2 py-1">Item</th><th className="px-2 py-1">MPR</th><th className="px-2 py-1">Project</th>
            <th className="px-2 py-1 text-right">Balance</th><th className="px-2 py-1 text-right">Order qty</th>
          </tr></thead>
          <tbody>{lines.map(l => (
            <tr key={key(l)} className="border-t border-slate-100" data-testid={`${testid}-line-${l.line_id}`}>
              <td className="px-2 py-1"><input type="checkbox" checked={st(l).on} onChange={e => setRow(l, { on: e.target.checked })} data-testid={`${testid}-tick-${l.line_id}`} /></td>
              <td className="px-2 py-1">{l.material_name}{l.on_open_rfq?.length ? <span className="ml-1 text-amber-700">(on {l.on_open_rfq.join(', ')})</span> : null}</td>
              <td className="px-2 py-1 font-mono">{l.mpr_no}</td><td className="px-2 py-1">{l.project_name}</td>
              <td className="px-2 py-1 text-right tabular-nums">{l.balance_qty} {l.uom}</td>
              <td className="px-2 py-1 text-right"><input type="number" min="0" max={l.balance_qty} step="any" value={st(l).qty}
                onChange={e => setRow(l, { qty: Math.min(l.balance_qty, Math.max(0, Number(e.target.value))) })}
                className="w-20 px-1.5 py-0.5 border border-slate-300 rounded-sm text-right" data-testid={`${testid}-qty-${l.line_id}`} /></td>
            </tr>))}
          </tbody>
        </table>
      )}
      {chosen.length > 0 && (
        <button type="button" onClick={add} className="px-3 py-1.5 bg-[#3b4872] text-white text-xs rounded-sm font-semibold" data-testid={`${testid}-add-btn`}>
          Add {chosen.length} line{chosen.length === 1 ? '' : 's'} to PO
        </button>
      )}
    </div>
  );
}
